import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import web5 from "../img/hos.jpg";
import "../style/styles.css";
import "../style/Hospital.css";

const Hospital = () => {
  const quickStats = [
    { value: "OPD", label: "Patient Management" },
    { value: "IPD", label: "Ward & Billing" },
    { value: "Lab", label: "Diagnostics Reports" },
    { value: "ERP", label: "Inventory & Accounts" },
  ];

  const coreModules = [
    {
      title: "OPD Management",
      text:
        "Manage patient registration, appointments, visiting records, doctor consultation information, vitals, clinical history, prescriptions, pharmacy and pathology billing.",
    },
    {
      title: "IPD Billing",
      text:
        "Online billing for admitted patients including room rent, operations, deliveries, oxygen, consultations, nursing fees, laboratory tests, X-rays, ultrasound, medicines and procedures.",
    },
    {
      title: "TPA & Insurance",
      text:
        "Manage patient billing, insurance claim workflow, TPA sanction amount, TPA records and insurance details for organized claim processing.",
    },
    {
      title: "User Management",
      text:
        "Add, modify and delete users with specific rights, revoke user access in emergencies and maintain user operational audit reports.",
    },
    {
      title: "Inventory Management",
      text:
        "Control indent, purchase order, approvals, GRN, online material request, issues, vendor management, purchase invoice and accounts payable.",
    },
    {
      title: "Financial Accounting",
      text:
        "Complete financial accounting including cash book, bank book, receivables, payables and reports up to final accounting.",
    },
  ];

  const patientFeatures = [
    "Patient Registration with unique registration number",
    "Patient appointment management",
    "Visiting records",
    "Doctor consultation information",
    "Doctor advice records",
    "Medicine records",
    "Prescription history",
    "Treatment history",
    "Billing history",
    "Front desk connected with important departments",
    "Patient inquiry handling",
    "Centralized billing support",
  ];

  const billingFeatures = [
    "OPD Billing",
    "IPD Billing",
    "Daycare Billing",
    "Pharmacy Billing",
    "Laboratory Billing",
    "OT Billing",
    "Emergency Billing",
    "Department-wise Billing",
    "IPD Collection",
    "Partial Payment",
    "Full Payment",
    "Refund",
    "Credit Note",
    "MIS report data collection",
  ];

  const clinicalFeatures = [
    "Medical record maintenance",
    "Medical history tracking",
    "Medication and allergy information",
    "Appointment date tracking",
    "Prescription records",
    "Doctor reminders",
    "Advice and next visit reminders",
    "Discharge card",
    "Medical certificate",
    "Nursing station services",
    "Medicine indent",
    "OT scheduling",
  ];

  const diagnosticsFeatures = [
    "Diagnostics Test Rate Master",
    "Investigation Master with Reference Values",
    "Appointments",
    "Test-wise Reports",
    "Billing & Receipts",
    "Centralized Billing",
    "Lab Report",
    "Patient Registration",
    "Package Master",
    "Test Summary",
  ];

  const inventoryFeatures = [
    "Indent to Purchase Order cycle",
    "Approvals and GRN",
    "Online Material Request",
    "Issue Management",
    "Paperless Inventory Workflow",
    "Flexible Item Grouping",
    "Multiple Units and Conversion Factor",
    "Multiple Store Options",
    "Vendor Management",
    "Purchase Invoice",
    "Accounts Payable",
  ];

  const automationFeatures = [
    {
      title: "Face Recognition Attendance",
      text:
        "WardMaster runs an Android app face recognition attendance system that provides robust and accurate employee attendance without complicated hardware.",
    },
    {
      title: "Financial Control",
      text:
        "HR, accounting and other modules are linked for effective financial control and user access control.",
    },
    {
      title: "Operational Efficiency",
      text:
        "Automation helps reduce manual tasks and allows hospital staff to focus more time on patient care and service delivery.",
    },
    {
      title: "Better Patient Care",
      text:
        "Access to patient medical history, medicines and allergy information helps doctors and staff provide better and safer care.",
    },
  ];

  const handleImageError = (event) => {
    event.currentTarget.style.display = "none";
  };

  return (
    <main className="hospital-detail-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          WardMaster - Hospital & Nursing Home Management System | EBS Software Solution
        </title>
        <meta
          name="description"
          content="WardMaster is a hospital and nursing home management system for OPD, IPD, patient records, appointments, medical billing, pharmacy, pathology, ward bed records, nursing, OT, discharge notes, inventory, accounting, user management and Android face recognition attendance."
        />
      </Helmet>

      <section className="hospital-hero">
        <img
          className="hospital-hero-bg"
          src={web5}
          alt="WardMaster Hospital and Nursing Home Management System"
          onError={handleImageError}
        />
        <div className="hospital-hero-overlay"></div>

        <Container className="hospital-hero-container">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <div className="hospital-hero-content">
                <div className="hospital-kicker">Hospital / Nursing Home Software</div>
                <h1>WardMaster</h1>
                <h2>Complete Solution for Hospital / Nursing Home Management System</h2>
                <p>
                  WardMaster manages OPD, IPD patient records, appointments,
                  medical and lab billing, prescribed medicines, payments, ward
                  and bed records, nursing, OT, discharge notes, inventory,
                  accounting and user control.
                </p>

                <div className="hospital-hero-actions">
                  <a href="#hospital-modules" className="hospital-btn hospital-btn-primary">
                    View Features
                  </a>
                  <Link to="/EmailContactForm" className="hospital-btn hospital-btn-outline">
                    Request Demo
                  </Link>
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <div className="hospital-highlight-card">
                <span>Healthcare ERP</span>
                <strong>OPD + IPD + Lab + Pharmacy + Accounts</strong>
                <p>
                  Designed for hospitals, nursing homes, diagnostics centres,
                  clinics and healthcare operations.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-stats-section">
        <Container>
          <Row className="g-3">
            {quickStats.map((item) => (
              <Col lg={3} md={6} key={item.label}>
                <div className="hospital-stat-card">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="hospital-overview-section">
        <Container>
          <Row className="g-4 align-items-center">
            <Col lg={6}>
              <div className="hospital-section-label">Overview</div>
              <h2 className="hospital-section-title">
                Complete patient management and hospital ERP platform
              </h2>
              <p className="hospital-text">
                WardMaster facilitates management of OPD and IPD patient health
                records, appointment scheduling, medical billing, lab billing,
                ward-bed records, prescriptions, payments, nursing, OT and
                discharge notes.
              </p>
              <p className="hospital-text">
                Patient Management Software helps healthcare professionals
                maintain accurate patient records, prescriptions, medical
                history, financial accounting, payments and insurance
                information from one organized system.
              </p>
            </Col>

            <Col lg={6}>
              <div className="hospital-overview-card">
                <h3>WardMaster covers</h3>
                <div className="hospital-overview-grid">
                  <div><span>✓</span><p>OPD</p></div>
                  <div><span>✓</span><p>IPD</p></div>
                  <div><span>✓</span><p>TPA</p></div>
                  <div><span>✓</span><p>Pharmacy</p></div>
                  <div><span>✓</span><p>Diagnostics</p></div>
                  <div><span>✓</span><p>Accounting</p></div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-modules-section" id="hospital-modules">
        <Container>
          <div className="hospital-heading-center">
            <div className="hospital-section-label">Key Features</div>
            <h2 className="hospital-section-title">
              Hospital workflow from registration to final accounting
            </h2>
            <p>
              WardMaster connects front desk, OPD, IPD, billing, inventory,
              diagnostics, user control and final accounting in one healthcare
              management system.
            </p>
          </div>

          <Row className="g-4">
            {coreModules.map((module, index) => (
              <Col lg={4} md={6} key={module.title}>
                <div className="hospital-module-card">
                  <div className="hospital-module-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <h3>{module.title}</h3>
                  <p>{module.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="hospital-feature-list-section">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <div className="hospital-list-card">
                <div className="hospital-section-label">Patient Management</div>
                <h2>Registration, appointments and clinical records</h2>
                <ul>
                  {patientFeatures.map((item) => (
                    <li key={item}><span>✓</span>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col lg={6}>
              <div className="hospital-list-card hospital-list-card-dark">
                <div className="hospital-section-label">Billing</div>
                <h2>OPD, IPD, pharmacy, lab and department billing</h2>
                <ul>
                  {billingFeatures.map((item) => (
                    <li key={item}><span>✓</span>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-feature-list-section hospital-feature-list-alt">
        <Container>
          <Row className="g-4">
            <Col lg={6}>
              <div className="hospital-list-card">
                <div className="hospital-section-label">Clinical Care</div>
                <h2>Medical history, prescription and OT workflow</h2>
                <ul>
                  {clinicalFeatures.map((item) => (
                    <li key={item}><span>✓</span>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col lg={6}>
              <div className="hospital-list-card">
                <div className="hospital-section-label">Diagnostics</div>
                <h2>Pathology and laboratory management</h2>
                <ul>
                  {diagnosticsFeatures.map((item) => (
                    <li key={item}><span>✓</span>{item}</li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-inventory-section">
        <Container>
          <Row className="g-4 align-items-start">
            <Col lg={5}>
              <div className="hospital-inventory-title">
                <div className="hospital-section-label">Inventory ERP</div>
                <h2 className="hospital-section-title">
                  Complete inventory cycle for hospital stores
                </h2>
                <p>
                  WardMaster supports hospital inventory from indent to purchase
                  order, approvals, GRN, online material request, issues, vendor
                  management and accounts payable.
                </p>
              </div>
            </Col>

            <Col lg={7}>
              <div className="hospital-inventory-grid">
                {inventoryFeatures.map((item) => (
                  <div key={item}>
                    <span>✓</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-automation-section">
        <Container>
          <Row className="g-4">
            <Col lg={5}>
              <div className="hospital-automation-title">
                <div className="hospital-section-label">Automation & Control</div>
                <h2 className="hospital-section-title">
                  Better patient care with operational automation
                </h2>
                <p>
                  WardMaster improves efficiency by automating routine work,
                  maintaining patient history and supporting secure user control
                  with Android face recognition attendance.
                </p>
              </div>
            </Col>

            <Col lg={7}>
              <Row className="g-3">
                {automationFeatures.map((feature) => (
                  <Col md={6} key={feature.title}>
                    <div className="hospital-automation-card">
                      <span>✓</span>
                      <h3>{feature.title}</h3>
                      <p>{feature.text}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="hospital-cta-section">
        <Container>
          <div className="hospital-cta-card">
            <div>
              <span>Ready to modernize your hospital operations?</span>
              <h2>Use WardMaster for OPD, IPD, billing, lab and accounts.</h2>
              <p>
                Get a complete healthcare management system for hospitals,
                nursing homes, clinics and diagnostics operations.
              </p>
            </div>
            <Link to="/EmailContactForm" className="hospital-btn hospital-btn-light">
              Request Demo
            </Link>
          </div>
        </Container>
      </section>
    </main>
  );
};

export default Hospital;
